// ============================================================
// calendarViewPref.ts
// Per-user, per-device preference: which Calendar/Diary layout is shown
// — 'month' (the grid), 'week' (7-day time-grid) or 'day' (single-day
// time-grid). Frontend-only, persisted in localStorage — same tiny-
// zustand pattern as calendarScopePref / calendarDarkPref, so the view
// toggle + the fetch range + the rendered layout all react live.
//
// DEFAULT: 'month' — the existing grid stays the landing view, so nothing
// changes for a user who never touches the toggle.
// ============================================================
import { create } from 'zustand'

export type CalendarViewMode = 'day' | 'week' | 'month'

const STORAGE_KEY = 'casevo_calendar_view'

const readView = (): CalendarViewMode => {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'day' || v === 'week' ? v : 'month'
  } catch {
    return 'month'
  }
}

interface CalendarViewState {
  view: CalendarViewMode
  setView: (v: CalendarViewMode) => void
}

export const useCalendarViewPref = create<CalendarViewState>((set) => ({
  view: readView(),
  setView: (v: CalendarViewMode) => {
    try {
      localStorage.setItem(STORAGE_KEY, v)
    } catch {
      /* storage blocked (private mode) — keep the in-memory value */
    }
    set({ view: v })
  },
}))
